/**
 * IA Credit Store — Zustand store for credit balance with Firestore real-time sync.
 *
 * Architecture:
 * - Firestore is the source of truth (backend deducts atomically via transactions)
 * - AuthContext's onSnapshot listener pushes real-time updates to userAccount.credits
 * - This store subscribes to userAccount and provides reactive credit state to UI
 * - Generates unique idempotency keys per request to prevent double-spend
 * - Supports optimistic deductions (UI updates immediately, backend confirms)
 *
 * Concurrency safety:
 * - Backend uses @firestore.transactional for atomic deductions (handles multiple windows)
 * - Frontend uses idempotency keys sent in request headers to prevent duplicate charges
 * - onSnapshot keeps all open windows in sync with the authoritative Firestore balance
 */

import { create } from 'zustand';
import { ModelTier, MODEL_TIERS, FREE_DAILY_CREDITS } from '../pages/UnifiedChat/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreditState {
  /** Current credit balance (from Firestore via onSnapshot). */
  balance: number;
  /** Total credits used today */
  totalUsedToday: number;
  /** Daily allowance for current plan */
  dailyAllowance: number;
  /** Current plan: 'free' | 'signals' | 'strategist' */
  plan: string;
  /** Last reset date (YYYY-MM-DD) */
  lastResetDate: string;
  /** Whether credit data has been loaded from Firestore */
  isLoaded: boolean;
  /** Whether user is authenticated (credits only apply to signed-in users) */
  isAuthenticated: boolean;

  // --- Actions ---

  /** Sync credits from Firestore userAccount snapshot. Called by AuthContext bridge. */
  syncFromFirestore: (credits: FirestoreCredits | undefined, isAuthenticated: boolean) => void;

  /** Check if user can afford a given credit cost. */
  canAfford: (cost: number) => boolean;

  /** Get credit cost for a model tier. */
  getTierCost: (tier: ModelTier) => number;

  /** Generate a unique idempotency key for a request. Prevents double-spend across windows. */
  generateRequestId: () => string;

  /** Optimistically deduct credits (UI-only, before backend confirms). */
  optimisticDeduct: (cost: number) => void;

  /** Roll back an optimistic deduction (on error). */
  rollbackDeduct: (cost: number) => void;

  /** Reset store (on logout). */
  reset: () => void;
}

/** Shape of Firestore users/{uid}.credits document */
export interface FirestoreCredits {
  balance: number;
  totalUsedToday: number;
  lastResetDate: string;
  plan: string;
}

// ---------------------------------------------------------------------------
// Idempotency key generator
// ---------------------------------------------------------------------------

let requestCounter = 0;

/**
 * Generate a unique request ID for credit deduction.
 * Format: ia_{timestamp}_{counter}_{random}
 * Sent as X-IA-Request-Id header to prevent duplicate charges.
 */
function generateRequestId(): string {
  requestCounter += 1;
  const timestamp = Date.now().toString(36);
  const counter = requestCounter.toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `ia_${timestamp}_${counter}_${random}`;
}

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

const DEFAULT_STATE = {
  balance: FREE_DAILY_CREDITS,
  totalUsedToday: 0,
  dailyAllowance: FREE_DAILY_CREDITS,
  plan: 'free',
  lastResetDate: '',
  isLoaded: false,
  isAuthenticated: false,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useCreditStore = create<CreditState>((set, get) => ({
  ...DEFAULT_STATE,

  syncFromFirestore: (credits, isAuthenticated) => {
    if (!isAuthenticated) {
      set({ ...DEFAULT_STATE, isLoaded: true, isAuthenticated: false });
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    if (!credits) {
      // New user or no credits doc yet — full daily allowance
      set({
        balance: FREE_DAILY_CREDITS,
        totalUsedToday: 0,
        dailyAllowance: FREE_DAILY_CREDITS,
        plan: 'free',
        lastResetDate: today,
        isLoaded: true,
        isAuthenticated: true,
      });
      return;
    }

    // Auto-reset if date changed (matches backend logic)
    const needsReset = credits.lastResetDate !== today;
    const planAllowance = getPlanAllowance(credits.plan);

    set({
      balance: needsReset ? planAllowance : credits.balance,
      totalUsedToday: needsReset ? 0 : credits.totalUsedToday,
      dailyAllowance: planAllowance,
      plan: credits.plan || 'free',
      lastResetDate: credits.lastResetDate || today,
      isLoaded: true,
      isAuthenticated: true,
    });
  },

  canAfford: (cost) => {
    const { balance, isAuthenticated } = get();
    if (!isAuthenticated) return true; // Guest gating is separate
    return balance >= cost;
  },

  getTierCost: (tier) => {
    return MODEL_TIERS.find(t => t.tier === tier)?.creditCost ?? 1;
  },

  generateRequestId: () => {
    return generateRequestId();
  },

  optimisticDeduct: (cost) => {
    set((state) => ({
      balance: Math.max(0, state.balance - cost),
      totalUsedToday: state.totalUsedToday + cost,
    }));
  },

  rollbackDeduct: (cost) => {
    set((state) => ({
      balance: state.balance + cost,
      totalUsedToday: Math.max(0, state.totalUsedToday - cost),
    }));
  },

  reset: () => {
    set(DEFAULT_STATE);
  },
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Plan daily allowance lookup (mirrors backend PLAN_DAILY_CREDITS).
 * Consolidated model: Free / Signals / Strategist. Legacy names alias upward. */
function getPlanAllowance(plan: string): number {
  switch (plan) {
    case 'signals':
    case 'premium':      // legacy alias
      return 1000;
    case 'strategist':
    case 'enterprise':   // legacy alias
      return 3000;
    case 'free':
    default:
      return 150;
  }
}

// ---------------------------------------------------------------------------
// Exports for request header
// ---------------------------------------------------------------------------

/** Header name for idempotency key. */
export const IA_REQUEST_ID_HEADER = 'X-IA-Request-Id';
